<script lang="ts">
	// The public start screen (D1). Two ways in: Google, and an email with a
	// password (UC-25). A new owner may create an account with an email
	// instead of Google (D17), and an owner who forgot a password asks for a
	// new one (D19, UC-26).
	//
	// Google leaves this origin. Its sign-in is an OAuth redirect, not a fetch:
	// the browser has to reach Google's consent screen itself and come back
	// with a code. The email form is a fetch, whose reply sets the session
	// cookie; the app then asks who is signed in, as after every way in.

	import Band from '../components/Band.svelte';
	import BrandMark from '../components/BrandMark.svelte';
	import Button from '../components/Button.svelte';
	import Field from '../components/Field.svelte';
	import { apiBase } from '../api/client';
	import { fill, message, type Language } from '../i18n/messages';
	import { carryEmail, signIn as passwordSignIn, type SignInResult } from '../owner/password';
	import { FORGOT_PATH, navigate, SIGN_UP_PATH } from '../router.svelte';

	interface Props {
		language: Language;
		/** The email form signed someone in; the app reloads the session. */
		onSignedIn: () => void;
	}

	let { language, onSignedIn }: Props = $props();

	// The page is about to leave for Google; the button says so rather than
	// taking a second press.
	let leaving = $state(false);

	let email = $state('');
	let password = $state('');
	let working = $state(false);
	let refusal = $state<SignInResult | null>(null);

	// Google came back with an address another owner holds (UC-28): this
	// person signed up another way. Or a Bosla admin's visit link was used,
	// too old or unknown (UC-64), and the API sent the browser here with no
	// session. Each is read once, then taken off the address bar, so a
	// reload does not say it again.
	let emailTaken = $state(false);
	let visitEnded = $state(false);
	$effect(() => {
		const said = new URLSearchParams(window.location.search).get('signin');
		if (said !== 'email-taken' && said !== 'visit-ended') return;
		if (said === 'email-taken') emailTaken = true;
		else visitEnded = true;
		window.history.replaceState(window.history.state, '', window.location.pathname);
	});

	const locked = $derived(refusal?.kind === 'locked');
	const ready = $derived(email.trim().length > 0 && password.length > 0 && !locked);

	const refusalText = $derived.by(() => {
		switch (refusal?.kind) {
			case 'failed':
				return message(language, 'signInFailed');
			case 'unverified':
				return message(language, 'signInUnverified');
			case 'locked':
				return fill(message(language, 'signInLocked'), refusal.minutes);
			case 'unreachable':
				return message(language, 'loadFailed');
			default:
				if (emailTaken) return message(language, 'signInEmailTaken');
				return visitEnded ? message(language, 'signInVisitEnded') : '';
		}
	});

	function signInWithGoogle() {
		leaving = true;
		window.location.assign(`${apiBase}/auth/google`);
	}

	async function signInWithPassword() {
		if (!ready || working) return;
		working = true;
		const result = await passwordSignIn(email, password);
		working = false;
		if (result.kind === 'signedIn') {
			onSignedIn();
			return;
		}
		refusal = result;
	}

	// Typing again ends a refusal, except a lock, which holds until it lifts.
	function edited() {
		if (refusal && refusal.kind !== 'locked') refusal = null;
		emailTaken = false;
		visitEnded = false;
	}

	function forgot() {
		carryEmail(email);
		navigate(FORGOT_PATH);
	}
</script>

<section class="screen" data-testid="screen-signin">
	<div class="card">
		<BrandMark size={26} />
		<div class="words">
			<h1>{message(language, 'signInTitle')}</h1>
			<p class="instruction">{message(language, 'signInInstruction')}</p>
		</div>
		<Button
			testId="signin-google"
			label={message(language, 'signInWithGoogle')}
			google
			wide
			state={leaving ? 'pending' : 'idle'}
			pendingLabel={message(language, 'signInOpening')}
			onPress={signInWithGoogle}
		/>
		<div class="or" aria-hidden="true">
			<span class="rule"></span><span>{message(language, 'signInOr')}</span><span class="rule"
			></span>
		</div>
		{#if refusalText}
			<Band tone="flag" testId="signin-refusal">{refusalText}</Band>
		{/if}
		<Field
			testId="signin-email"
			label={message(language, 'signInEmail')}
			type="email"
			autocomplete="email"
			mono
			maxLength={254}
			value={email}
			onInput={(value) => {
				email = value;
				edited();
			}}
		/>
		<Field
			testId="signin-password"
			label={message(language, 'signInPassword')}
			type="password"
			autocomplete="current-password"
			secret
			mono
			maxLength={128}
			value={password}
			onInput={(value) => {
				password = value;
				edited();
			}}
		/>
		<div class="row">
			<Button
				testId="signin-forgot"
				label={message(language, 'signInForgot')}
				tone="text"
				size="sm"
				onPress={forgot}
			/>
		</div>
		<Button
			testId="signin-password-submit"
			label={message(language, 'signInSubmit')}
			tone="ink"
			wide
			disabled={!ready}
			state={working ? 'pending' : 'idle'}
			pendingLabel={message(language, 'signInSigningIn')}
			onPress={() => void signInWithPassword()}
		/>
		<div class="foot">
			<span>{message(language, 'signInNew')}</span>
			<Button
				testId="signin-create-account"
				label={message(language, 'signInCreateAccount')}
				tone="text"
				size="md"
				onPress={() => navigate(SIGN_UP_PATH)}
			/>
		</div>
	</div>
</section>

<style>
	.screen {
		min-block-size: 100dvh;
		display: grid;
		place-items: center;
		padding: var(--space-9);
		background: var(--surface-page);
	}

	.card {
		inline-size: 100%;
		max-inline-size: 420px;
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
		background: var(--surface-card);
		border: var(--border-width) solid var(--border-default);
		border-radius: var(--radius-panel);
		padding: var(--card-pad);
	}

	.words {
		display: flex;
		flex-direction: column;
		gap: var(--space-3);
	}

	h1 {
		margin: 0;
		font-size: var(--text-h3);
		font-weight: 700;
		letter-spacing: var(--tracking-tight);
		line-height: var(--leading-headline);
	}

	.instruction {
		margin: 0;
		font-size: var(--text-base);
		line-height: var(--leading-snug);
		color: var(--text-body);
		text-wrap: pretty;
	}

	.or {
		display: flex;
		align-items: center;
		gap: var(--space-4);
		font-size: var(--text-2xs);
		color: var(--text-muted);
	}

	.rule {
		flex: 1;
		border-block-start: var(--border-width) solid var(--border-inset);
	}

	.row {
		display: flex;
	}

	.foot {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: var(--space-2);
		padding-block-start: var(--space-5);
		border-block-start: var(--border-width) solid var(--border-inset);
		font-size: var(--text-sm);
		color: var(--text-body);
	}
</style>
